import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import PasswordInput from "../components/ui/PasswordInput";
import Toast, { ToastMessage } from "../components/ui/Toast";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import DriverAdminDetailModal from "../components/admin/driver/DriverAdminDetailModal";
import { validatePassword } from "../utils/passwordPolicy";

type UserRole = "ADMIN" | "SELLER" | "DRIVER";

type UserApiRow = {
  id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  rol?: string;
  active?: boolean | null;
  created_at?: string;
  updated_at?: string;
};

type UserItem = {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  rol: UserRole | string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type UsersResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  usuarios?: UserApiRow[];
  user?: UserApiRow;
  userID?: number;
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  userID?: number;
  sellerID?: number;
  driverID?: number;
  adminID?: number;
};

type DriversResponse = {
  ok?: boolean;
  error?: string;
  drivers?: Array<Record<string, unknown>>;
  driver?: Record<string, unknown>;
  data?: Array<Record<string, unknown>> | Record<string, unknown>;
  result?: Array<Record<string, unknown>> | Record<string, unknown>;
};

type CreateFormState = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  rol: UserRole;
  password: string;
};

type SellerExtraState = {
  CUIT: string;
  razon_social: string;
  descuento: string;
  condicionIVA: string;
};

type DriverExtraState = {
  tarifaID: string;
  vehiculo: string;
  patente: string;
  DNI: string;
  CUIL: string;
  alias: string;
  CBU: string;
  comision: string;
  photos: {
    DNI_frente: string;
    DNI_dorso: string;
    cedula_frente: string;
    cedula_dorso: string;
    registro_frente: string;
    registro_dorso: string;
    selfie: string;
  };
};

type AdminProfileName = "PICKER" | "COORDINADOR" | "LIQUIDACIONES" | "ADMIN_TOTAL";

type AdminExtraState = {
  db_access: boolean;
  user_access: boolean;
  warehouse_access: boolean;
  finance_access: boolean;
  // Etiqueta informativa (ver informe "Sprint Operativo") — nunca la
  // fuente de permisos, eso siguen siendo los 4 booleanos de arriba.
  // Precarga el formulario con un preset; las capacidades se pueden seguir
  // tocando individualmente después (excepciones), sin perder el label.
  profile: AdminProfileName | null;
  // Warehouse asignado (ver informe "LYNX — Multi-Warehouse, Dropoffs,
  // Pickers y Estanterías", PR 2) — exclusivo de profile='PICKER', nunca
  // un selector para el operario: lo asigna quien administra Usuarios.
  warehouse_id: number | null;
};

type WarehouseOption = { id: number; name: string; type: "CROSSDOCK" | "DROPOFF" };

// Preset de capacidades por perfil — solo precarga el formulario, nunca es
// la fuente de verdad (eso siguen siendo los checkboxes, que quedan
// editables después de elegir un perfil).
// Coordinador necesita db_access (ver informe "Liquidaciones — Cierre de
// la semana", Parte 6): Lista Legacy y el detalle de un envío — desde
// donde se crea un Reporte Operativo — están gateados por esa capacidad
// (GET /v1/envios-legacy, ver server/legacy/enviosList.routes.js). Sin
// esto, un Coordinador nunca podía llegar a la pantalla real desde la que
// se supone que reporta.
const PROFILE_PRESETS: Record<AdminProfileName, Omit<AdminExtraState, "profile">> = {
  PICKER: { db_access: false, user_access: false, warehouse_access: true, finance_access: false, warehouse_id: null },
  COORDINADOR: { db_access: true, user_access: false, warehouse_access: false, finance_access: false, warehouse_id: null },
  LIQUIDACIONES: { db_access: false, user_access: false, warehouse_access: false, finance_access: true, warehouse_id: null },
  ADMIN_TOTAL: { db_access: true, user_access: true, warehouse_access: true, finance_access: true, warehouse_id: null },
};

const PROFILE_LABELS: Record<AdminProfileName, string> = {
  PICKER: "Picker",
  COORDINADOR: "Coordinador",
  LIQUIDACIONES: "Liquidaciones",
  ADMIN_TOTAL: "Administrador Total",
};

type EditFormState = {
  id: number | null;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  rol: UserRole;
};

const emptyCreateForm: CreateFormState = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  rol: "SELLER",
  password: "",
};

const emptySellerExtra: SellerExtraState = {
  CUIT: "",
  razon_social: "",
  descuento: "0",
  condicionIVA: "Responsable Inscripto",
};

const emptyDriverExtra: DriverExtraState = {
  tarifaID: "",
  vehiculo: "",
  patente: "",
  DNI: "",
  CUIL: "",
  alias: "",
  CBU: "",
  comision: "0.25",
  photos: {
    DNI_frente: "",
    DNI_dorso: "",
    cedula_frente: "",
    cedula_dorso: "",
    registro_frente: "",
    registro_dorso: "",
    selfie: "",
  },
};

const emptyAdminExtra: AdminExtraState = {
  db_access: true,
  user_access: true,
  // El depósito se da a propósito, no por ser admin.
  warehouse_access: false,
  finance_access: false,
  profile: null,
  warehouse_id: null,
};

const emptyEditForm: EditFormState = {
  id: null,
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  rol: "SELLER",
};

const commissionOptions = [
  { label: "30%", value: "0.30" },
  { label: "25%", value: "0.25" },
  { label: "20%", value: "0.20" },
  { label: "15%", value: "0.15" },
  { label: "10%", value: "0.10" },
  { label: "5%", value: "0.05" },
];

const DRIVER_TARIFA_MODE_NONE = "";
const DRIVER_TARIFA_MODE_MANUAL = "__MANUAL__";

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.65)",
  padding: "16px",
};

const modalCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "980px",
  maxHeight: "85vh",
  overflow: "hidden",
  padding: 0,
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid var(--border)",
  padding: "18px 20px",
};

const modalBodyStyle: React.CSSProperties = {
  maxHeight: "75vh",
  overflowY: "auto",
  padding: "20px",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
};

const infoBoxStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

function buildFullName(firstName?: string, lastName?: string) {
  return [firstName || "", lastName || ""]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" ");
}

function normalizeUser(row: UserApiRow): UserItem {
  return {
    id: Number(row.id),
    username: row.username || "",
    firstName: row.first_name || "",
    lastName: row.last_name || "",
    fullName: buildFullName(row.first_name, row.last_name),
    email: row.email || "",
    phone: row.phone || "",
    rol: (row.rol || "").toUpperCase(),
    active: row.active === false ? false : true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function roleLabel(role: string) {
  const upper = String(role || "").toUpperCase();
  if (upper === "ADMIN") return "Administrador";
  if (upper === "SELLER") return "Seller";
  if (upper === "DRIVER") return "Chofer";
  return upper || "—";
}

function roleHelper(role: UserRole) {
  if (role === "ADMIN") return "Acceso completo al panel administrativo.";
  if (role === "SELLER") return "Se creará usuario base + seller_data.";
  if (role === "DRIVER") return "Se creará usuario base + driver_data.";
  return "";
}

function formatDate(value?: string) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-AR");
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "block",
        marginBottom: "8px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

// Perfil + permisos de un admin — reutilizado por el alta (renderCreateStep2)
// y por la edición de un admin existente (ver informe "Sprint Operativo",
// Parte 3: antes no existía NINGÚN camino para editar capacidades después
// del alta). Elegir un perfil precarga los 4 booleanos; se pueden seguir
// tocando individualmente después sin perder flexibilidad.
function AdminPermissionsFields({
  value,
  onChange,
  warehouses = [],
}: {
  value: AdminExtraState;
  onChange: (next: AdminExtraState) => void;
  warehouses?: WarehouseOption[];
}) {
  const setField = (field: keyof Omit<AdminExtraState, "profile" | "warehouse_id">, checked: boolean) => {
    onChange({ ...value, [field]: checked });
  };
  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div style={infoBoxStyle}>
        <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "10px" }}>
          Perfil
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
          {(Object.keys(PROFILE_LABELS) as AdminProfileName[]).map((p) => {
            const active = value.profile === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onChange({ ...PROFILE_PRESETS[p], profile: p })}
                style={{
                  padding: "8px 14px",
                  borderRadius: "10px",
                  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
                  background: active ? "var(--primary-soft)" : "transparent",
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                }}
              >
                {PROFILE_LABELS[p]}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: "12px", color: "var(--text-secondary)", margin: "6px 0 0" }}>
          Elegir un perfil precarga los permisos de abajo — se pueden seguir ajustando individualmente como excepción.
        </p>
      </div>

      {value.profile === "PICKER" ? (
        <div style={infoBoxStyle}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "10px" }}>
            Warehouse asignado
          </div>
          <select
            value={value.warehouse_id ?? ""}
            onChange={(e) => onChange({ ...value, warehouse_id: e.target.value ? Number(e.target.value) : null })}
            style={selectStyle}
          >
            <option value="">Elegir Warehouse…</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name} ({w.type === "DROPOFF" ? "Dropoff" : "Crossdock"})</option>
            ))}
          </select>
          <p style={{ fontSize: "12px", color: "var(--text-secondary)", margin: "6px 0 0" }}>
            Define en qué Warehouse recepciona este Picker. Se asigna acá, no la elige el operario.
          </p>
        </div>
      ) : null}

      <div style={infoBoxStyle}>
        <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "10px" }}>
          Permisos del admin
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={value.db_access} onChange={(e) => setField("db_access", e.target.checked)} />
          Acceso de base de datos
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: "10px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={value.user_access} onChange={(e) => setField("user_access", e.target.checked)} />
          Acceso de gestión de usuarios
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={value.warehouse_access} onChange={(e) => setField("warehouse_access", e.target.checked)} />
          Acceso al depósito (planta y estanterías)
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={value.finance_access} onChange={(e) => setField("finance_access", e.target.checked)} />
          Acceso financiero (Liquidaciones)
        </label>
      </div>
    </div>
  );
}

function Select({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label style={{ display: "block" }}>
      <FieldLabel>{label}</FieldLabel>
      <select {...props} style={selectStyle}>
        {children}
      </select>
    </label>
  );
}

function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={{ position: "absolute", inset: 0 }} onClick={onClose} />
      <div style={{ position: "relative", width: "100%", maxWidth: "980px" }}>
        <Card style={modalCardStyle}>
          <div style={modalHeaderStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "26px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Creación y administración de accesos por rol.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={modalBodyStyle}>{children}</div>
        </Card>
      </div>
    </div>
  );
}

export default function Users() {
  const { token } = useAuth();

  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchText, setSearchText] = useState("");
  const [searchRole, setSearchRole] = useState<string>("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm);
  // Confirmación de contraseña (ver informe "LYNX — Fase 1.1: Usuarios y
  // Seguridad"): a propósito NO vive en CreateFormState — es un campo de
  // validación pura, nunca se manda al backend, no tiene sentido mezclarlo
  // con el payload que sí viaja en el POST.
  const [createConfirmPassword, setCreateConfirmPassword] = useState("");
  const [createPasswordError, setCreatePasswordError] = useState<string | null>(null);
  const [sellerExtra, setSellerExtra] = useState<SellerExtraState>(emptySellerExtra);
  const [driverExtra, setDriverExtra] = useState<DriverExtraState>(emptyDriverExtra);
  const [adminExtra, setAdminExtra] = useState<AdminExtraState>(emptyAdminExtra);
  const [driverTarifaMode, setDriverTarifaMode] = useState<string>(DRIVER_TARIFA_MODE_NONE);

  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);
  // Cambiar contraseña desde Editar Usuario (ver informe "LYNX — Fase 1.1"):
  // sección independiente a propósito — ambos vacíos = no se toca la
  // contraseña; nunca se mezcla con editForm ni con el PUT de datos
  // generales (se manda como una request aparte, ver handleEditUser).
  const [editNewPassword, setEditNewPassword] = useState("");
  const [editConfirmPassword, setEditConfirmPassword] = useState("");
  const [editPasswordError, setEditPasswordError] = useState<string | null>(null);
  // Datos de admin_data del usuario en edición — antes no existía NINGÚN
  // camino para editar capacidades después del alta (ver informe "Sprint
  // Operativo"). adminEditDataId es el id de la fila admin_data (no el
  // user_id), que es lo que exige PUT /v2/admins/:id.
  const [adminEditExtra, setAdminEditExtra] = useState<AdminExtraState | null>(null);
  const [adminEditDataId, setAdminEditDataId] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  // Toast (ver informe "LYNX — Fase 1.1", ajuste "No usar alert()"): un
  // solo mensaje a la vez, alcanza para este alcance (Usuarios).
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [driverDetailOpen, setDriverDetailOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<{
    driverId: number | null;
    userId: number | null;
    username: string;
  }>({
    driverId: null,
    userId: null,
    username: "",
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await authFetch("/v2/users", {
        token,
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudieron cargar los usuarios");
      }

      const rows = Array.isArray(json?.usuarios) ? json.usuarios : [];
      setUsers(rows.map(normalizeUser));
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Opciones para el selector de Warehouse de un Picker (ver informe "LYNX
  // — Multi-Warehouse, Dropoffs, Pickers y Estanterías", PR 2) — lista
  // chica, se carga una vez; GET /v2/warehouses acepta db_access O
  // user_access, así que cualquiera que pueda llegar a esta pantalla puede
  // verla.
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  useEffect(() => {
    if (!token) return;
    authFetch("/v2/warehouses", { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) return;
        setWarehouseOptions((payload.warehouses ?? []).filter((w: any) => w.active));
      })
      .catch(() => {});
  }, [token]);

  const visibleUsers = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return users
      .filter((user) => {
        if (!normalizedSearch) return true;

        return [
          user.username,
          user.firstName,
          user.lastName,
          user.fullName,
          user.email,
          user.phone,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearch));
      })
      .filter((user) =>
        searchRole.trim() ? user.rol === searchRole.trim().toUpperCase() : true
      );
  }, [users, searchText, searchRole]);

  const totals = useMemo(() => {
    return {
      total: users.length,
      admins: users.filter((u) => u.rol === "ADMIN").length,
      sellers: users.filter((u) => u.rol === "SELLER").length,
      drivers: users.filter((u) => u.rol === "DRIVER").length,
      active: users.filter((u) => u.active).length,
    };
  }, [users]);

  const clearFilters = () => {
    setSearchText("");
    setSearchRole("");
  };

  const resetCreateWizard = () => {
    setCreateForm(emptyCreateForm);
    setCreateConfirmPassword("");
    setCreatePasswordError(null);
    setSellerExtra(emptySellerExtra);
    setDriverExtra(emptyDriverExtra);
    setAdminExtra(emptyAdminExtra);
    setDriverTarifaMode(DRIVER_TARIFA_MODE_NONE);
    setCreateStep(1);
  };

  const openCreateModal = () => {
    resetCreateWizard();
    setCreateOpen(true);
  };

  const openEditModal = (user: UserItem) => {
    setEditForm({
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      rol: (user.rol as UserRole) || "SELLER",
    });
    setAdminEditExtra(null);
    setAdminEditDataId(null);
    setEditNewPassword("");
    setEditConfirmPassword("");
    setEditPasswordError(null);
    setEditOpen(true);

    // Reutiliza GET /v2/admins (ya existe, usado hoy para otra cosa) en vez
    // de un endpoint nuevo por user_id — la lista de admins es chica.
    if (user.rol === "ADMIN" && token) {
      authFetch("/v2/admins", { token })
        .then(async (res) => {
          const payload = await res.json().catch(() => ({}));
          if (!res.ok || payload?.ok === false) return;
          const row = (payload.admins || []).find((a: any) => String(a.user_id) === String(user.id));
          if (row) {
            setAdminEditDataId(row.id);
            setAdminEditExtra({
              db_access: row.db_access === true,
              user_access: row.user_access === true,
              warehouse_access: row.warehouse_access === true,
              finance_access: row.finance_access === true,
              profile: row.profile ?? null,
              warehouse_id: row.warehouse_id ?? null,
            });
          }
        })
        .catch(() => {});
    }
  };

  const validateCreateBasic = () => {
    if (!createForm.username.trim()) {
      alert("Completá el username.");
      return false;
    }
    if (!createForm.firstName.trim()) {
      alert("Completá el nombre.");
      return false;
    }
    if (!createForm.lastName.trim()) {
      alert("Completá el apellido.");
      return false;
    }
    if (!createForm.email.trim()) {
      alert("Completá el email.");
      return false;
    }
    if (!createForm.phone.trim()) {
      alert("Completá el teléfono.");
      return false;
    }
    // Contraseña: validación propia, con mensaje inline (ver informe "LYNX
    // — Fase 1.1") — separada del resto (que usa alert()) a propósito, no
    // se mezcla esa lógica.
    const policyResult = validatePassword(createForm.password);
    if (!policyResult.valid) {
      setCreatePasswordError(policyResult.message);
      return false;
    }
    if (createForm.password !== createConfirmPassword) {
      setCreatePasswordError("Las contraseñas no coinciden.");
      return false;
    }
    setCreatePasswordError(null);
    return true;
  };

  const validateRoleExtras = () => {
    if (createForm.rol === "SELLER") {
      if (!sellerExtra.razon_social.trim()) {
        alert("Completá la razón social del seller.");
        return false;
      }
      if (!sellerExtra.CUIT.trim()) {
        alert("Completá el CUIT del seller.");
        return false;
      }
    }

    if (createForm.rol === "DRIVER") {
      if (!driverExtra.vehiculo.trim()) {
        alert("Completá el vehículo.");
        return false;
      }
      if (!driverExtra.DNI.trim()) {
        alert("Completá el DNI.");
        return false;
      }
      if (!driverExtra.comision.trim()) {
        alert("Completá la comisión.");
        return false;
      }
      if (
        driverTarifaMode === DRIVER_TARIFA_MODE_MANUAL &&
        !driverExtra.tarifaID.trim()
      ) {
        alert("Completá el Tarifa ID o elegí la opción Ninguna.");
        return false;
      }
    }

    return true;
  };

  const validateEdit = () => {
    if (!editForm.username.trim()) {
      alert("Completá el username.");
      return false;
    }
    if (!editForm.firstName.trim()) {
      alert("Completá el nombre.");
      return false;
    }
    if (!editForm.lastName.trim()) {
      alert("Completá el apellido.");
      return false;
    }
    if (!editForm.email.trim()) {
      alert("Completá el email.");
      return false;
    }
    if (!editForm.phone.trim()) {
      alert("Completá el teléfono.");
      return false;
    }
    return true;
  };

  // Cambiar contraseña desde Editar Usuario (ver informe "LYNX — Fase
  // 1.1"): validación propia, separada de validateEdit — reglas exactas
  // pedidas: ambos vacíos = no se toca nada (true, sin error); uno solo
  // completo = error; no coinciden = error; no cumple la política = error.
  const validateEditPassword = () => {
    if (!editNewPassword && !editConfirmPassword) {
      setEditPasswordError(null);
      return true;
    }
    if (!editNewPassword || !editConfirmPassword) {
      setEditPasswordError("Completá ambos campos para cambiar la contraseña.");
      return false;
    }
    if (editNewPassword !== editConfirmPassword) {
      setEditPasswordError("Las contraseñas no coinciden.");
      return false;
    }
    const policyResult = validatePassword(editNewPassword);
    if (!policyResult.valid) {
      setEditPasswordError(policyResult.message);
      return false;
    }
    setEditPasswordError(null);
    return true;
  };

  const goToCreateStep2 = () => {
    if (!validateCreateBasic()) return;
    setCreateStep(2);
  };

  const rollbackUserIfNeeded = useCallback(
    async (userID: number | null) => {
      if (!token || !userID) return;

      try {
        await authFetch(`/v2/users/${userID}`, {
          method: "DELETE",
          token,
        });
      } catch (rollbackError) {
        console.error("Rollback delete user failed:", rollbackError);
      }
    },
    [token]
  );

  const createRoleData = async (userID: number) => {
    if (createForm.rol === "SELLER") {
      const payload = {
        userID,
        CUIT: sellerExtra.CUIT.trim(),
        razon_social: sellerExtra.razon_social.trim(),
        descuento: Number(sellerExtra.descuento || "0"),
        condicionIVA: sellerExtra.condicionIVA.trim(),
      };

      const res = await authFetch("/v2/sellers", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo crear seller_data");
      }

      return;
    }

    if (createForm.rol === "ADMIN") {
      const payload = {
        userID,
        db_access: adminExtra.db_access,
        user_access: adminExtra.user_access,
        warehouse_access: adminExtra.warehouse_access,
        finance_access: adminExtra.finance_access,
        profile: adminExtra.profile,
        warehouse_id: adminExtra.warehouse_id,
      };

      const res = await authFetch("/v2/admins", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo crear admin_data");
      }

      return;
    }

    if (createForm.rol === "DRIVER") {
      const normalizedTarifaId =
        driverTarifaMode === DRIVER_TARIFA_MODE_MANUAL && driverExtra.tarifaID.trim()
          ? Number(driverExtra.tarifaID)
          : null;

      // DNI/CUIL son bigint en driver_data — "" rompe el INSERT con un error
      // de sintaxis de Postgres (ver informe "Sprint Final — Cierre de
      // Liquidaciones", mismo bug que en DriverAdminDetailModal.tsx).
      const payload = {
        userID,
        tarifaID: normalizedTarifaId,
        vehiculo: driverExtra.vehiculo.trim(),
        patente: driverExtra.patente.trim(),
        DNI: driverExtra.DNI.trim() || null,
        CUIL: driverExtra.CUIL.trim() || null,
        alias: driverExtra.alias.trim(),
        CBU: driverExtra.CBU.trim(),
        comision: Number(driverExtra.comision || "0"),
        photos: {
          DNI_frente: driverExtra.photos.DNI_frente,
          DNI_dorso: driverExtra.photos.DNI_dorso,
          cedula_frente: driverExtra.photos.cedula_frente,
          cedula_dorso: driverExtra.photos.cedula_dorso,
          registro_frente: driverExtra.photos.registro_frente,
          registro_dorso: driverExtra.photos.registro_dorso,
          selfie: driverExtra.photos.selfie,
        },
      };

      const res = await authFetch("/v2/drivers", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(
          json?.error ||
            "No se pudo crear driver_data. Revisá comisión y el manejo actual de tarifa_id en backend."
        );
      }
    }
  };

  const handleCreateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateCreateBasic()) return;
    if (!validateRoleExtras()) return;

    setSubmitting(true);

    let createdUserId: number | null = null;

    try {
      const payload = {
        username: createForm.username.trim(),
        first_name: createForm.firstName.trim(),
        last_name: createForm.lastName.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim(),
        rol: createForm.rol,
        password: createForm.password,
      };

      const res = await authFetch("/v2/users", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false || !json?.userID) {
        throw new Error(json?.error || "No se pudo crear el usuario");
      }

      createdUserId = Number(json.userID);

      await createRoleData(createdUserId);

      setCreateOpen(false);
      resetCreateWizard();
      setToast({ variant: "success", title: "Usuario creado correctamente." });
      await loadUsers();
    } catch (e) {
      console.error(e);

      if (createdUserId) {
        await rollbackUserIfNeeded(createdUserId);
      }

      setToast({
        variant: "error",
        title: "No se pudo crear el usuario.",
        detail: `Motivo: ${e instanceof Error ? e.message : "Error creando usuario"}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateEdit() || !editForm.id) return;
    if (!validateEditPassword()) return;

    setSubmitting(true);

    // Ver informe "LYNX — Fase 1.1", ajuste "No usar alert()": el guardado
    // son hasta 3 requests independientes (datos generales, permisos de
    // admin, contraseña) — el usuario tiene que ver explícitamente cuál de
    // esas 3 se guardó y cuál no, nunca un mensaje genérico que mezcle todo.
    const willChangePassword = Boolean(editNewPassword);
    const willChangePerms = editForm.rol === "ADMIN" && Boolean(adminEditDataId) && Boolean(adminEditExtra);
    let generalSaved = false;
    let permsSaved = false;
    let passwordSaved = false;

    try {
      const payload = {
        user: {
          username: editForm.username.trim(),
          first_name: editForm.firstName.trim(),
          last_name: editForm.lastName.trim(),
          email: editForm.email.trim(),
          phone: editForm.phone.trim(),
          rol: editForm.rol,
        },
      };

      const res = await authFetch(`/v2/users/${editForm.id}`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo actualizar el usuario");
      }
      generalSaved = true;

      // Capacidades de admin: solo si es ADMIN y ya se cargaron (evita
      // mandar un PUT con datos a medio cargar si la fila todavía no llegó).
      if (willChangePerms) {
        const capRes = await authFetch(`/v2/admins/${adminEditDataId}`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify({ admin: adminEditExtra }),
        });
        const capJson: GenericResponse = await capRes.json().catch(() => ({}));
        if (!capRes.ok || capJson?.ok === false) {
          throw new Error(capJson?.error || "No se pudieron actualizar los permisos del admin");
        }
        permsSaved = true;
      }

      // Cambiar contraseña: request independiente, solo si el usuario
      // completó los dos campos (ver validateEditPassword) — nunca se
      // manda junto con el payload de datos generales.
      if (willChangePassword) {
        const pwRes = await authFetch(`/v2/users/${editForm.id}/change-password`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify({ password: editNewPassword }),
        });
        const pwJson: GenericResponse = await pwRes.json().catch(() => ({}));
        if (!pwRes.ok || pwJson?.ok === false) {
          throw new Error(pwJson?.error || "No se pudo actualizar la contraseña");
        }
        passwordSaved = true;
      }

      setEditOpen(false);
      setEditForm(emptyEditForm);
      setAdminEditExtra(null);
      setAdminEditDataId(null);
      setEditNewPassword("");
      setEditConfirmPassword("");
      setEditPasswordError(null);
      setToast({ variant: "success", title: "Usuario actualizado correctamente." });
      await loadUsers();
    } catch (e) {
      console.error(e);
      const errorMessage = e instanceof Error ? e.message : "Error actualizando usuario";

      if (!generalSaved) {
        // Nada se guardó todavía — error puro, sin checklist (no hay nada
        // que reportar como parcialmente exitoso).
        setToast({ variant: "error", title: "No se pudo actualizar el usuario.", detail: `Motivo: ${errorMessage}` });
      } else {
        // Al menos los datos generales se guardaron — el usuario tiene que
        // ver explícitamente qué de todo lo que intentó guardar quedó bien
        // y qué no, nunca asumir que "falló" significa que no se guardó nada.
        setToast({
          variant: "warning",
          title: "Algunos cambios no se guardaron.",
          lines: [
            { label: "Datos generales (nombre, email, teléfono, rol)", ok: generalSaved },
            ...(willChangePerms ? [{ label: "Permisos de administrador", ok: permsSaved }] : []),
            ...(willChangePassword ? [{ label: "Contraseña", ok: passwordSaved }] : []),
          ],
          detail: `Motivo: ${errorMessage}`,
        });
      }
      // El modal se queda abierto a propósito (ver informe) — el usuario
      // corrige lo que falló y reintenta sin perder lo que ya escribió.
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (user: UserItem) => {
    const ok = window.confirm(`¿Desactivar el usuario "${user.username}"?`);
    if (!ok) return;

    try {
      const res = await authFetch(`/v2/users/${user.id}/deactivate`, {
        method: "PUT",
        token,
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo desactivar el usuario");
      }

      await loadUsers();
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : "Error desactivando usuario");
    }
  };

  const handleReactivate = async (user: UserItem) => {
    const ok = window.confirm(`¿Reactivar el usuario "${user.username}"?`);
    if (!ok) return;

    try {
      let res = await authFetch(`/v2/users/${user.id}/reactivate`, {
        method: "PUT",
        token,
      });

      if (res.status === 404 || res.status === 405) {
        res = await authFetch(`/v2/users/${user.id}/activate`, {
          method: "PUT",
          token,
        });
      }

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo reactivar el usuario");
      }

      await loadUsers();
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : "Error reactivando usuario");
    }
  };

  const handleResetPassword = async (user: UserItem) => {
    // Ver informe "LYNX — Fase 1.2.9": el texto anterior decía "a 0000",
    // pero el backend resetea a process.env.RESET_PASSWORD (un valor
    // distinto, que el frontend nunca debe conocer ni mostrar) — eso era
    // la causa real de que el login fallara después de un reset: el admin
    // intentaba entrar con "0000" creyendo que era la contraseña real.
    // Como además el valor de reset queda marcado como contraseña insegura
    // (ver ForcedPasswordChange.tsx), el propio usuario la va a tener que
    // cambiar en su próximo login — no hace falta ni tiene sentido
    // mostrarla acá.
    const ok = window.confirm(
      `¿Reestablecer la contraseña de "${user.username}"? El usuario deberá definir una nueva contraseña la próxima vez que inicie sesión.`
    );
    if (!ok) return;

    try {
      const res = await authFetch(`/v2/users/${user.id}/reset-password`, {
        method: "PUT",
        token,
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo reestablecer la contraseña");
      }

      alert(`Contraseña reestablecida para ${user.username}. Va a tener que definir una nueva en su próximo inicio de sesión.`);
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : "Error reestableciendo contraseña");
    }
  };

  const handleDeleteUser = async (user: UserItem) => {
    const ok = window.confirm(`¿Eliminar definitivamente el usuario "${user.username}"?`);
    if (!ok) return;

    try {
      const res = await authFetch(`/v2/users/${user.id}`, {
        method: "DELETE",
        token,
      });

      const json: UsersResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo eliminar el usuario");
      }

      await loadUsers();
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : "Error eliminando usuario");
    }
  };

  const handleOpenDriverDetail = async (user: UserItem) => {
    try {
      const res = await authFetch("/v2/drivers", {
        token,
      });

      const json: DriversResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo cargar la lista de drivers");
      }

      const rowsSource =
        (Array.isArray(json?.drivers) && json.drivers) ||
        (Array.isArray(json?.data) && json.data) ||
        (Array.isArray(json?.result) && json.result) ||
        [];

      const match = rowsSource.find((row) => {
        const rowUserId =
          row?.userID != null
            ? Number(row.userID)
            : row?.user_id != null
            ? Number(row.user_id)
            : null;

        return rowUserId === user.id;
      });

      if (!match || match?.id == null) {
        throw new Error(
          "No se encontró driver_data para este usuario. Puede haberse creado el user sin completar la segunda parte."
        );
      }

      setSelectedDriver({
        driverId: Number(match.id),
        userId: user.id,
        username: user.username,
      });
      setDriverDetailOpen(true);
    } catch (e) {
      console.error(e);
      alert(
        e instanceof Error
          ? e.message
          : "Error cargando el detalle del chofer"
      );
    }
  };

  const renderCreateStep2 = () => {
    if (createForm.rol === "SELLER") {
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "16px",
          }}
          className="users-form-grid"
        >
          <div>
            <FieldLabel>Razón social *</FieldLabel>
            <Input
              value={sellerExtra.razon_social}
              onChange={(e) =>
                setSellerExtra((prev) => ({ ...prev, razon_social: e.target.value }))
              }
              placeholder="Love is back"
            />
          </div>

          <div>
            <FieldLabel>CUIT *</FieldLabel>
            <Input
              value={sellerExtra.CUIT}
              onChange={(e) =>
                setSellerExtra((prev) => ({ ...prev, CUIT: e.target.value }))
              }
              placeholder="30-71234567-8"
            />
          </div>

          <div>
            <FieldLabel>Condición IVA</FieldLabel>
            <Input
              value={sellerExtra.condicionIVA}
              onChange={(e) =>
                setSellerExtra((prev) => ({ ...prev, condicionIVA: e.target.value }))
              }
              placeholder="Responsable Inscripto"
            />
          </div>

          <div>
            <FieldLabel>Descuento</FieldLabel>
            <Input
              value={sellerExtra.descuento}
              onChange={(e) =>
                setSellerExtra((prev) => ({ ...prev, descuento: e.target.value }))
              }
              placeholder="0"
            />
          </div>
        </div>
      );
    }

    if (createForm.rol === "ADMIN") {
      return <AdminPermissionsFields value={adminExtra} onChange={setAdminExtra} warehouses={warehouseOptions} />;
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: "16px",
        }}
        className="users-form-grid"
      >
        <Select
          label="Tarifa"
          value={driverTarifaMode}
          onChange={(e) => {
            const nextMode = e.target.value;
            setDriverTarifaMode(nextMode);

            if (nextMode === DRIVER_TARIFA_MODE_NONE) {
              setDriverExtra((prev) => ({ ...prev, tarifaID: "" }));
            }
          }}
        >
          <option value={DRIVER_TARIFA_MODE_NONE}>Ninguna</option>
          <option value={DRIVER_TARIFA_MODE_MANUAL}>Ingresar ID manualmente</option>
        </Select>

        <div>
          <FieldLabel>Vehículo *</FieldLabel>
          <Input
            value={driverExtra.vehiculo}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, vehiculo: e.target.value }))
            }
            placeholder="Moto / Auto / Utilitario"
          />
        </div>

        {driverTarifaMode === DRIVER_TARIFA_MODE_MANUAL ? (
          <div>
            <FieldLabel>Tarifa ID *</FieldLabel>
            <Input
              value={driverExtra.tarifaID}
              onChange={(e) =>
                setDriverExtra((prev) => ({ ...prev, tarifaID: e.target.value }))
              }
              placeholder="1"
            />
          </div>
        ) : (
          <div style={infoBoxStyle}>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "var(--text-primary)",
                marginBottom: "6px",
              }}
            >
              Tarifa desacoplada
            </div>
            <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              Este driver se creará sin tarifa asociada. El front enviará <b>tarifaID: null</b>.
            </div>
          </div>
        )}

        <div>
          <FieldLabel>Patente</FieldLabel>
          <Input
            value={driverExtra.patente}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, patente: e.target.value }))
            }
            placeholder="A123BCD"
          />
        </div>

        <div>
          <FieldLabel>DNI *</FieldLabel>
          <Input
            value={driverExtra.DNI}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, DNI: e.target.value }))
            }
            placeholder="30111222"
          />
        </div>

        <div>
          <FieldLabel>CUIL</FieldLabel>
          <Input
            value={driverExtra.CUIL}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, CUIL: e.target.value }))
            }
            placeholder="20-30111222-3"
          />
        </div>

        <div>
          <FieldLabel>Alias</FieldLabel>
          <Input
            value={driverExtra.alias}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, alias: e.target.value }))
            }
            placeholder="danielita.lynx"
          />
        </div>

        <div>
          <FieldLabel>CBU</FieldLabel>
          <Input
            value={driverExtra.CBU}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, CBU: e.target.value }))
            }
            placeholder="2850590940090418135201"
          />
        </div>

        <div>
          <FieldLabel>Comisión (decimal)</FieldLabel>
          <Input
            value={driverExtra.comision}
            onChange={(e) =>
              setDriverExtra((prev) => ({ ...prev, comision: e.target.value }))
            }
            placeholder="0.25"
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldLabel>Comisión rápida</FieldLabel>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {commissionOptions.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={
                  driverExtra.comision === option.value ? "primary" : "secondary"
                }
                onClick={() =>
                  setDriverExtra((prev) => ({ ...prev, comision: option.value }))
                }
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div style={{ marginTop: "10px", fontSize: "12px", color: "var(--text-secondary)" }}>
            Se envía en decimal. Ejemplo: 25% → 0.25
          </div>
        </div>

        <div style={{ ...infoBoxStyle, gridColumn: "1 / -1" }}>
          <div
            style={{
              fontSize: "13px",
              fontWeight: 700,
              color: "var(--text-primary)",
              marginBottom: "6px",
            }}
          >
            Nota importante
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
            Si falla la creación de <b>driver_data</b>, ahora el sistema intentará borrar el
            usuario base para evitar inconsistencias.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={pageWrapStyle}>
      <Toast message={toast} onClose={() => setToast(null)} />

      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Usuarios</h1>
          <p style={subtitleStyle}>
            Creación y administración de accesos para administración, sellers y choferes.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={loadUsers}>
            Actualizar
          </Button>
          <Button onClick={openCreateModal}>Nuevo usuario</Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="users-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Usuarios totales</div>
          <div style={kpiValueStyle}>{totals.total}</div>
          <div style={kpiMetaStyle}>Cantidad total registrada.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Administradores</div>
          <div style={kpiValueStyle}>{totals.admins}</div>
          <div style={kpiMetaStyle}>Usuarios con rol ADMIN.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Sellers</div>
          <div style={kpiValueStyle}>{totals.sellers}</div>
          <div style={kpiMetaStyle}>Usuarios con rol SELLER.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Choferes</div>
          <div style={kpiValueStyle}>{totals.drivers}</div>
          <div style={kpiMetaStyle}>Usuarios con rol DRIVER.</div>
        </Card>
      </div>

      <Card>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
          className="users-filters-grid"
        >
          <div>
            <FieldLabel>Buscar por usuario, nombre, email o teléfono</FieldLabel>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Ej: juan, juanperez o correo@dominio.com"
            />
          </div>

          <Select
            label="Rol"
            value={searchRole}
            onChange={(e) => setSearchRole(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="ADMIN">Administrador</option>
            <option value="SELLER">Seller</option>
            <option value="DRIVER">Chofer</option>
          </Select>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" fullWidth onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Listado de usuarios</h2>
          <p style={sectionTextStyle}>
            Gestión de usuarios, rol, estado, contraseña y edición de datos base.
          </p>
        </div>

        {error ? (
          <div style={{ color: "var(--danger)", fontSize: "14px", marginBottom: "14px" }}>
            {error}
          </div>
        ) : null}

        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Username</th>
                <th style={thStyle}>Nombre</th>
                <th style={thStyle}>Apellido</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Teléfono</th>
                <th style={thStyle}>Rol</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Actualizado</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    Cargando usuarios...
                  </td>
                </tr>
              ) : visibleUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    No hay usuarios para los filtros actuales.
                  </td>
                </tr>
              ) : (
                visibleUsers.map((user) => (
                  <tr key={user.id}>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>{user.id}</td>
                    <td style={tdStyle}>{user.username}</td>
                    <td style={tdStyle}>{user.firstName || "—"}</td>
                    <td style={tdStyle}>{user.lastName || "—"}</td>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                      {user.email || "—"}
                    </td>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                      {user.phone || "—"}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "30px",
                          padding: "0 12px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background: "var(--primary-soft)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {roleLabel(user.rol)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          color: user.active ? "var(--success)" : "var(--danger)",
                          fontWeight: 700,
                          fontSize: "13px",
                        }}
                      >
                        {user.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                      {formatDate(user.updatedAt || user.createdAt)}
                    </td>
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {user.rol === "DRIVER" ? (
                          <Button
                            variant="secondary"
                            onClick={() => handleOpenDriverDetail(user)}
                            style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                          >
                            Detalle driver
                          </Button>
                        ) : null}

                        <Button
                          variant="secondary"
                          onClick={() => openEditModal(user)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Editar
                        </Button>

                        <Button
                          variant="secondary"
                          onClick={() => handleResetPassword(user)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Reset
                        </Button>

                        {user.active ? (
                          <Button
                            variant="secondary"
                            onClick={() => handleDeactivate(user)}
                            style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                          >
                            Desactivar
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            onClick={() => handleReactivate(user)}
                            style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                          >
                            Reactivar
                          </Button>
                        )}

                        <Button
                          variant="danger"
                          onClick={() => handleDeleteUser(user)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={createOpen}
        title={createStep === 1 ? "Nuevo usuario" : "Datos específicos del rol"}
        onClose={() => !submitting && setCreateOpen(false)}
      >
        <form onSubmit={handleCreateUser}>
          {createStep === 1 ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "16px",
                }}
                className="users-form-grid"
              >
                <div>
                  <FieldLabel>Username</FieldLabel>
                  <Input
                    value={createForm.username}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, username: e.target.value }))
                    }
                    placeholder="usuario_demo"
                    autoComplete="off"
                  />
                </div>

                <div />

                <div>
                  <FieldLabel>Contraseña inicial</FieldLabel>
                  <PasswordInput
                    value={createForm.password}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, password: e.target.value }));
                      setCreatePasswordError(null);
                    }}
                    placeholder="Ingresá una contraseña"
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <FieldLabel>Confirmar contraseña</FieldLabel>
                  <PasswordInput
                    value={createConfirmPassword}
                    onChange={(e) => {
                      setCreateConfirmPassword(e.target.value);
                      setCreatePasswordError(null);
                    }}
                    placeholder="Repetí la contraseña"
                    autoComplete="new-password"
                    error={createPasswordError || undefined}
                  />
                </div>

                <div>
                  <FieldLabel>Nombre</FieldLabel>
                  <Input
                    value={createForm.firstName}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    placeholder="Juan"
                  />
                </div>

                <div>
                  <FieldLabel>Apellido</FieldLabel>
                  <Input
                    value={createForm.lastName}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    placeholder="Pérez"
                  />
                </div>

                <div>
                  <FieldLabel>Email</FieldLabel>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="correo@dominio.com"
                  />
                </div>

                <div>
                  <FieldLabel>Teléfono</FieldLabel>
                  <Input
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    placeholder="11XXXXXXXX"
                  />
                </div>

                <Select
                  label="Rol"
                  value={createForm.rol}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, rol: e.target.value as UserRole }))
                  }
                >
                  <option value="ADMIN">Administrador</option>
                  <option value="SELLER">Seller</option>
                  <option value="DRIVER">Chofer</option>
                </Select>

                <div style={infoBoxStyle}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                      marginBottom: "6px",
                    }}
                  >
                    Permisos del rol
                  </div>
                  <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                    {roleHelper(createForm.rol)}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "24px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCreateOpen(false)}
                  disabled={submitting}
                >
                  Cancelar
                </Button>

                <Button type="button" onClick={goToCreateStep2} disabled={submitting}>
                  Continuar
                </Button>
              </div>
            </>
          ) : (
            <>
              {renderCreateStep2()}

              <div
                style={{
                  marginTop: "24px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCreateStep(1)}
                  disabled={submitting}
                >
                  Volver
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCreateOpen(false)}
                  disabled={submitting}
                >
                  Cancelar
                </Button>

                <Button type="submit" disabled={submitting}>
                  {submitting ? "Creando..." : "Finalizar creación"}
                </Button>
              </div>
            </>
          )}
        </form>
      </Modal>

      <Modal
        open={editOpen}
        title="Editar usuario"
        onClose={() => !submitting && setEditOpen(false)}
      >
        <form onSubmit={handleEditUser}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
            }}
            className="users-form-grid"
          >
            <div>
              <FieldLabel>Username</FieldLabel>
              <Input
                value={editForm.username}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, username: e.target.value }))
                }
                placeholder="usuario_demo"
                autoComplete="off"
              />
            </div>

            <div>
              <Select
                label="Rol"
                value={editForm.rol}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, rol: e.target.value as UserRole }))
                }
              >
                <option value="ADMIN">Administrador</option>
                <option value="SELLER">Seller</option>
                <option value="DRIVER">Chofer</option>
              </Select>
            </div>

            <div>
              <FieldLabel>Nombre</FieldLabel>
              <Input
                value={editForm.firstName}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, firstName: e.target.value }))
                }
                placeholder="Juan"
              />
            </div>

            <div>
              <FieldLabel>Apellido</FieldLabel>
              <Input
                value={editForm.lastName}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
                }
                placeholder="Pérez"
              />
            </div>

            <div>
              <FieldLabel>Email</FieldLabel>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="correo@dominio.com"
              />
            </div>

            <div>
              <FieldLabel>Teléfono</FieldLabel>
              <Input
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                }
                placeholder="11XXXXXXXX"
              />
            </div>
          </div>

          {editForm.rol === "ADMIN" && (
            <div style={{ marginTop: "20px" }}>
              {adminEditExtra ? (
                <AdminPermissionsFields value={adminEditExtra} onChange={setAdminEditExtra} warehouses={warehouseOptions} />
              ) : (
                <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Cargando permisos…</div>
              )}
            </div>
          )}

          {/* Cambiar contraseña (ver informe "LYNX — Fase 1.1: Usuarios y
              Seguridad"): sección independiente a propósito — ambos campos
              vacíos = no se toca la contraseña; nunca comparte validación
              ni payload con el resto del formulario (ver handleEditUser). */}
          <div
            style={{
              marginTop: "20px",
              paddingTop: "20px",
              borderTop: "1px solid var(--border)",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0", fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" }}>
              Cambiar contraseña
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "16px",
              }}
              className="users-form-grid"
            >
              <div>
                <FieldLabel>Nueva contraseña</FieldLabel>
                <PasswordInput
                  value={editNewPassword}
                  onChange={(e) => {
                    setEditNewPassword(e.target.value);
                    setEditPasswordError(null);
                  }}
                  placeholder="Dejar vacío para no cambiarla"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <FieldLabel>Confirmar nueva contraseña</FieldLabel>
                <PasswordInput
                  value={editConfirmPassword}
                  onChange={(e) => {
                    setEditConfirmPassword(e.target.value);
                    setEditPasswordError(null);
                  }}
                  placeholder="Repetí la nueva contraseña"
                  autoComplete="new-password"
                  error={editPasswordError || undefined}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>

            <Button type="submit" disabled={submitting}>
              {submitting ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </Modal>

      <DriverAdminDetailModal
        open={driverDetailOpen}
        token={token}
        driverId={selectedDriver.driverId}
        userId={selectedDriver.userId}
        username={selectedDriver.username}
        onClose={() => {
          setDriverDetailOpen(false);
          setSelectedDriver({
            driverId: null,
            userId: null,
            username: "",
          });
        }}
        onUpdated={loadUsers}
      />

      <style>
        {`
          .users-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .users-filters-grid {
            grid-template-columns: 2fr 1fr 1fr;
          }

          .users-form-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .users-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .users-filters-grid {
              grid-template-columns: 1fr 1fr !important;
            }
          }

          @media (max-width: 720px) {
            .users-kpi-grid,
            .users-filters-grid,
            .users-form-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}